/**
 * A scenario as data: the English scenario, its translations and the record that says who owns it
 * and who may run it. The ten catalogue scenarios are compiled into the Worker; everything a
 * facilitator forks or writes is a document like this, stored in D1 and resolved by
 * `src/worker/lib/scenario-source.ts`. The room snapshots whichever it is given, so the shape is
 * the same on every path.
 *
 * Custom ids are `u-` followed by 12 lowercase base-32 characters: still kebab-case, so URLs and
 * reports keep working, and impossible to confuse with a catalogue id.
 */
import { z } from "zod";
import type { TranslationMap } from "./localize";
import { flattenTranslatable } from "./localize";
import { parseScenario, type Scenario, type ScenarioInput } from "./schema";

export const Visibility = z.enum(["private", "unlisted", "listed"]);
export type Visibility = z.infer<typeof Visibility>;

export const ScenarioStatus = z.enum(["draft", "published", "deleted"]);
export type ScenarioStatus = z.infer<typeof ScenarioStatus>;

/** Where a runnable scenario came from, as the picker groups it. */
export type ScenarioOrigin = "catalogue" | "mine" | "community" | "shared";

export interface ScenarioMeta {
  id: string;
  ownerId: string;
  /** Reserved for organisations; null until they exist. */
  orgId: string | null;
  visibility: Visibility;
  status: ScenarioStatus;
  /** Version of the last published copy; the draft's own version lives in the scenario body. */
  publishedVersion: string | null;
  /** The scenario this one was forked from, when it was. */
  sourceId: string | null;
  sourceTitle: string | null;
  sourceVersion: string | null;
  approvedBy: string | null;
  approvedAt: number | null;
  createdAt: number;
  updatedAt: number;
  publishedAt: number | null;
}

export interface ScenarioTranslation {
  locale: string;
  map: TranslationMap;
  /** Fingerprints of the English each path was translated from; see `src/shared/translate`. */
  fingerprints: Record<string, string>;
  updatedAt: number;
}

/** What `GET /api/scenarios/:id/export` returns and `POST /api/scenarios/import` accepts. */
export interface ScenarioDocument {
  format: "tabletop-scenario";
  formatVersion: 1;
  scenario: ScenarioInput;
  translations: Record<string, TranslationMap>;
  /** Present on export; ignored on import except `sourceId`/`sourceTitle`/`sourceVersion`. */
  meta?: Partial<ScenarioMeta>;
}

export const ScenarioDocumentSchema = z.object({
  format: z.literal("tabletop-scenario"),
  formatVersion: z.literal(1),
  scenario: z.unknown(),
  translations: z.record(z.string(), z.record(z.string(), z.string())).default({}),
  meta: z
    .object({
      sourceId: z.string().nullable().optional(),
      sourceTitle: z.string().nullable().optional(),
      sourceVersion: z.string().nullable().optional(),
    })
    .partial()
    .optional(),
});

const ID_ALPHABET = "abcdefghjkmnpqrstuvwxyz23456789";
export const CUSTOM_ID_PREFIX = "u-";

export function isCustomScenarioId(id: string): boolean {
  return id.startsWith(CUSTOM_ID_PREFIX);
}

/** A fresh custom id from a source of random bytes (crypto in the Worker, seeded in tests). */
export function newScenarioId(random: (n: number) => Uint8Array): string {
  const bytes = random(12);
  let out = CUSTOM_ID_PREFIX;
  for (const b of bytes) out += ID_ALPHABET[b % ID_ALPHABET.length];
  return out;
}

/** Semantic-ish bump: 1.0.0 → 1.0.1; anything that is not three numbers gets ".1" appended. */
export function bumpVersion(version: string): string {
  const m = version.match(/^(\d+)\.(\d+)\.(\d+)$/);
  if (!m) return `${version}.1`;
  return `${m[1]}.${m[2]}.${Number(m[3]) + 1}`;
}

export interface ForkInput {
  source: Scenario;
  sourceTranslations: Record<string, TranslationMap>;
  /** Fingerprints per locale for the source; forks of the catalogue carry none and get them computed. */
  id: string;
  title?: string;
}

export interface ForkResult {
  scenario: Scenario;
  translations: Record<string, TranslationMap>;
  source: { id: string; title: string; version: string };
}

/**
 * Copy a runnable scenario into a new one. The copy keeps every translation the source had: they
 * are exact for everything the author leaves alone, and the delta planner reports only what the
 * author changes as outdated, because the fingerprints are taken from the source's English now.
 * Translated titles are kept too — the fork is a translation-complete twin until it is edited.
 */
export function forkScenario(input: ForkInput): ForkResult {
  const copy = structuredClone(input.source);
  copy.id = input.id;
  copy.version = "1.0.0";
  if (input.title) copy.title = input.title;
  const translations: Record<string, TranslationMap> = {};
  const valid = new Set(Object.keys(flattenTranslatable(copy)));
  for (const [locale, map] of Object.entries(input.sourceTranslations)) {
    const kept: TranslationMap = {};
    for (const [path, text] of Object.entries(map)) {
      // A renamed title would carry the old translation; drop it so the delta says "missing".
      if (!valid.has(path)) continue;
      if (path === "title" && input.title) continue;
      kept[path] = text;
    }
    translations[locale] = kept;
  }
  return {
    scenario: parseScenario(copy),
    translations,
    source: { id: input.source.id, title: input.source.title, version: input.source.version },
  };
}
